import { Dispatch } from 'react'
import { WppActionButton, WppIconTrash, WppInput, WppListItem, WppSelect } from '@wppopen/components-library-react'
import { getType } from 'utils'
import KeywordInput from '../KeywordInput/KeywordInput'
import {
  formHelperFunctions,
  type RetailerFormReducerAction,
} from 'reducers/CreateAndEditRetailerReducer/CreateAndEditRetailerReducer'
import type { ConfigGETResponseSuccess } from '@contracts'
import {
  brandLanguageFormHelperFunctions,
  BrandLanguageFormReducerState,
  UpdateBrandLanguageInputField,
  type UpdateBrandLanguageMultiInputField,
} from 'reducers/CreateAndEditRetailerReducer/RetailerBrandLanguageReducer'
import { BlockTypes } from 'reducers/CreateAndEditRetailerReducer/utils'
import styles from './BrandLanguageBlock.module.scss'
import { BrandFormReducerAction } from 'reducers/CreateAndEditBrandReducer/CreateAndEditBrandReducer'
import { StateToTypeMap } from 'reducers/FormReducer/FormReducer'

interface BrandLanguageBlockProps<
  T extends keyof Pick<StateToTypeMap, 'RetailerFormReducerState' | 'BrandFormReducerState'>,
> {
  disabled: boolean
  formReducer: [StateToTypeMap[T], Dispatch<RetailerFormReducerAction | BrandFormReducerAction>]
  configData: ConfigGETResponseSuccess | null
  idx: number
}
export default function BrandLanguageBlock<
  T extends keyof Pick<StateToTypeMap, 'RetailerFormReducerState' | 'BrandFormReducerState'>,
>({ disabled, formReducer, idx, configData }: BrandLanguageBlockProps<T>) {
  const [formReducerState, formReducerDispatch] = formReducer
  const { setBrandLanguage, removeFormBlock } = formHelperFunctions
  const { handleError, updateSingleInput, updateSelect, handleWarning, addInputItem, removeInputItem } =
    brandLanguageFormHelperFunctions
  const { brand_language } = formReducerState
  const correctBrandLanguage = brand_language[idx]

  return (
    <div className={styles.brand_language_container}>
      <WppActionButton
        disabled={disabled}
        aria-label="remove brand language"
        data-testid="remove-brand-language-cta"
        onClick={() => formReducerDispatch(removeFormBlock(BlockTypes.BRAND_LANGUAGE, idx))}
      >
        <WppIconTrash slot="icon-start" />
      </WppActionButton>
      <div>
        <div className={styles.half_size_inputs}>
          <WppSelect
            name="region"
            required
            value={correctBrandLanguage.region.value}
            labelConfig={{ text: 'Region' }}
            data-testid="brand-language-region-select"
            messageType={!correctBrandLanguage.region.error ? undefined : 'error'}
            message={correctBrandLanguage.region.error || ''}
            disabled={disabled}
            onWppChange={e => {
              const { value } = e.detail
              formReducerDispatch(setBrandLanguage(updateSelect('region', value), idx))
            }}
          >
            <WppListItem value="all" data-testid="brand-language-region-option">
              <p slot="label">All regions (general)</p>
            </WppListItem>
            {configData?.regions?.map(({ name, _id }) => (
              <WppListItem key={_id} value={_id} data-testid="brand-language-region-option">
                <p slot="label">{name}</p>
              </WppListItem>
            ))}
          </WppSelect>

          <WppInput
            name="replacement"
            required
            placeholder="Enter brand term e.g. XAIA"
            value={correctBrandLanguage.replacement.value}
            labelConfig={{ text: 'Brand term' }}
            messageType={!correctBrandLanguage.replacement.error ? undefined : 'error'}
            message={correctBrandLanguage.replacement.error}
            onWppChange={e => {
              const { value, name } = e.target
              if (value && correctBrandLanguage.replacement.error) {
                formReducerDispatch(
                  setBrandLanguage(handleError(name as keyof BrandLanguageFormReducerState, undefined), idx),
                )
              }
              formReducerDispatch(
                setBrandLanguage(updateSingleInput(name as UpdateBrandLanguageInputField, value), idx),
              )
            }}
            data-testid="brand-language-replacement-input"
            disabled={disabled}
          />
        </div>

        <KeywordInput<'BrandLanguageFormReducerState', T>
          id={getType<UpdateBrandLanguageMultiInputField>('patterns')}
          inputData={correctBrandLanguage.patterns}
          label="Terms to be replaced"
          disabled={disabled}
          formReducer={formReducer}
          isKeyword
          isRequired
          onWarning={(id, warning) => {
            formReducerDispatch(setBrandLanguage(handleWarning(id, warning), idx))
          }}
          onSubmit={(id, value) => {
            formReducerDispatch(setBrandLanguage(addInputItem(id, value), idx))
          }}
          onRemovePill={(id, index) => {
            formReducerDispatch(setBrandLanguage(removeInputItem(id, index), idx))
          }}
          onError={(id, error) => {
            formReducerDispatch(setBrandLanguage(handleError(id, error), idx))
          }}
        />
      </div>
    </div>
  )
}
